import { AudiencePlan } from '../../types'
import { motion } from 'framer-motion'
import { Users, Target, TrendingUp } from 'lucide-react'

interface AudiencePlannerProps {
  data: AudiencePlan
}

export function AudiencePlanner({ data }: AudiencePlannerProps) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading audience planner...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900 rounded-lg shadow-sm p-3 lg:p-4"
      >
        <h2 className="text-base lg:text-lg font-bold text-white mb-0.5">Audience Planner</h2>
        <p className="text-xs text-gray-300">Strategic audience targeting and segmentation</p>
      </motion.div>

      {/* Audience Segments */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border p-4"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2 text-blue-600" />
          Audience Segments
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(data.segments || []).map((segment, index) => (
            <div key={index} className="p-4 border rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">{segment.name}</h4>
              <p className="text-sm text-gray-600 mb-3">{segment.description}</p>
              
              <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                <div>
                  <span className="text-gray-500">Size:</span>
                  <span className="ml-2 font-medium">{segment.size.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Age:</span>
                  <span className="ml-2 font-medium">{segment.demographics.age}</span>
                </div>
              </div>

              <div className="mb-3">
                <div className="text-sm text-gray-500 mb-1">Interests</div>
                <div className="flex flex-wrap gap-1">
                  {segment.interests.slice(0, 3).map((interest, i) => (
                    <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Personas */}
      {data.personas && data.personas.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border p-4"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2 text-green-600" />
            Audience Personas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.personas.map((persona, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">{persona.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{persona.description}</p>
                
                <div className="mb-3">
                  <div className="text-sm font-medium text-gray-700 mb-1">Pain Points</div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {persona.painPoints.slice(0, 2).map((point, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-2">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Motivations</div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {persona.motivations.slice(0, 2).map((motivation, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-2">•</span>
                        {motivation}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Targeting Strategy */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm border p-4"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
          Targeting Strategy
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Primary Targeting</h4>
            <div className="space-y-2">
              {data.targeting.primary.map((method, index) => (
                <div key={index} className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  <span className="text-sm text-gray-700">{method}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Secondary Targeting</h4>
            <div className="space-y-2">
              {data.targeting.secondary.map((method, index) => (
                <div key={index} className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  <span className="text-sm text-gray-700">{method}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

